import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { useDarkMode } from "@/hooks/useDarkMode"
import useScrollLock from "@/hooks/useScrollLock"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, onClick, ...props }, ref) => {
  const darkMode = useDarkMode();
  
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        "fixed inset-0 z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 cursor-pointer",
        className
      )}
      style={{
        background: darkMode.isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)'
      }}
      onClick={onClick}
      {...props} />
  );
})
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

// Radix only renders DialogPrimitive.Content's children while the dialog is
// open, so mounting this inside Content scopes the scroll lock to open state.
// (The outer DialogContent wrapper stays mounted even when closed — locking
// there froze scrolling on pages with persistent closed dialogs.)
function DialogScrollLock() {
  useScrollLock(true);
  return null;
}

const DialogContent = React.forwardRef(({ className, children, style, ...props }, ref) => {
  const darkMode = useDarkMode();

  // Merge default background with custom style
  const defaultStyle = {
    background: darkMode.isDark ? '#2D4354' : 'white',
    borderColor: darkMode.isDark ? '#FED7A5' : '#E5E7EB'
  };
  
  const mergedStyle = { ...defaultStyle, ...style };

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          !darkMode.isDark && "force-light",
          "fixed left-[50%] top-[50%] z-[60] grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg transition-colors",
          className
        )}
        style={mergedStyle}
        {...props}>
        <DialogScrollLock />
        {children}
        <DialogPrimitive.Close
          asChild>
          <button
            type="button"
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            style={{cursor: 'pointer', background: 'transparent', border: 'none'}}>
            <X className="h-4 w-4" style={{color: darkMode.isDark ? '#FED7A5' : '#20212B'}} />
            <span className="sr-only">Close</span>
          </button>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const darkMode = useDarkMode();
  
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn("text-lg font-semibold leading-none tracking-tight transition-colors", className)}
      style={{color: darkMode.textPrimary}}
      {...props} />
  );
})
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const darkMode = useDarkMode();
  
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn("text-sm transition-colors", className)}
      style={{color: darkMode.textSecondary}}
      {...props} />
  );
})
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
